import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { buildImportSummary } from "./importAdapters";

const ImportDialog = ({ visible, stage, preview, onHide, onFileSelected, onCommit }) => {
  const [approvedKeys, setApprovedKeys] = React.useState(() => new Set());

  React.useEffect(() => {
    if (!preview) {
      setApprovedKeys(new Set());
      return;
    }
    setApprovedKeys(new Set((preview.newBands ?? []).map((b) => b.columnKey)));
  }, [preview]);

  const summary = React.useMemo(() => buildImportSummary(preview), [preview]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) onFileSelected(file);
  };

  const handleToggleBand = (columnKey) => {
    setApprovedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(columnKey)) next.delete(columnKey); else next.add(columnKey);
      return next;
    });
  };

  const commitDisabled = !preview?.canCommit || stage === "committing";

  return (
    <Dialog
      header="Импорт тарифов из файла"
      visible={visible}
      onHide={onHide}
      style={{ width: "60vw", maxWidth: "900px" }}
      modal
    >
      {stage === "idle" && (
        <div style={{ padding: "12px" }}>
          <label htmlFor="import-file" style={{ display: "block", marginBottom: 8 }}>
            Выберите файл (.xlsx или .csv)
          </label>
          <input
            id="import-file"
            type="file"
            accept=".xlsx,.csv"
            aria-label="Импорт из файла"
            onChange={handleFileChange}
          />
        </div>
      )}

      {stage === "uploading" && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}

      {stage === "preview" && preview && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
            <SummaryTile label="Обновлений" value={summary.updates} />
            <SummaryTile label="Новых ячеек" value={summary.inserts} />
            <SummaryTile label="Новых диапазонов" value={summary.newBands} />
            <SummaryTile label="Активных (изм.)" value={summary.activeChanges} />
            <SummaryTile label="Пропущено (др. перевозчик)" value={summary.skipped} />
            <SummaryTile label="Неизв. КАТО" value={summary.unknown} tone={summary.unknown ? "error" : "ok"} />
            <SummaryTile label="Ошибок" value={summary.errors} tone={summary.errors ? "error" : "ok"} />
          </div>

          {preview.unknownKatos?.length > 0 && (
            <div role="alert" style={{ background: "#fef2f2", padding: 10, borderRadius: 8, marginBottom: 12 }}>
              <strong>Импорт невозможен.</strong> Неизвестные КАТО:
              <code style={{ display: "block", marginTop: 4 }}>{preview.unknownKatos.join(", ")}</code>
            </div>
          )}

          {preview.newBands?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong>Новые диапазоны</strong>
              {preview.newBands.map((band) => (
                <label key={band.columnKey} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                  <Checkbox
                    inputId={`band-${band.columnKey}`}
                    checked={approvedKeys.has(band.columnKey)}
                    onChange={() => handleToggleBand(band.columnKey)}
                  />
                  <span id={`band-label-${band.columnKey}`}>Создать диапазон <strong>{band.label}</strong></span>
                  <input type="checkbox"
                    aria-label={band.label}
                    checked={approvedKeys.has(band.columnKey)}
                    onChange={() => handleToggleBand(band.columnKey)}
                    style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                  />
                </label>
              ))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button label="Отмена" severity="secondary" onClick={onHide} />
            <Button
              label="Подтвердить импорт"
              disabled={commitDisabled}
              onClick={() => onCommit([...approvedKeys])}
            />
          </div>
        </div>
      )}

      {stage === "committing" && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}
    </Dialog>
  );
};

const SummaryTile = ({ label, value, tone = "default" }) => (
  <div style={{
    padding: "10px 12px",
    borderRadius: 8,
    background: tone === "error" ? "#fef2f2" : "#eff6ff",
    border: `1px solid ${tone === "error" ? "#fecaca" : "#bfdbfe"}`,
  }}>
    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
    <div style={{ fontSize: "1.35rem", fontWeight: 600 }}>{value}</div>
  </div>
);

export default ImportDialog;
